"use client";

import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import { Navbar } from "./Navbar";
import { ActivityTracker } from "./ActivityTracker";
import { ANON_NAV_STATE, type NavState } from "@/lib/nav-state";
import { identifyUser } from "@/lib/analytics";
import { fetchNavStateOnce } from "@/lib/nav-state-request";

// Client island wrapping the navbar. The root layout stays static so every
// page can be served from the CDN; logged-in personalisation arrives after
// hydration via /api/nav-state.

// Supabase auth cookies (set by @supabase/ssr) are readable from JS. Checking
// locally lets anonymous visitors skip the nav-state fetch entirely, so public
// traffic never invokes a server function for the navbar.
function hasAuthCookie(): boolean {
  return /(^|;\s*)sb-[^=]*-auth-token(\.\d+)?=/.test(document.cookie);
}

export function NavbarIsland() {
  const [nav, setNav] = useState<NavState>(ANON_NAV_STATE);
  const pathname = usePathname();

  useEffect(() => {
    if (!hasAuthCookie()) return;
    let cancelled = false;
    // Shared with the Companion frame, so the app view never spends two nav-state reads.
    fetchNavStateOnce()
      .then((data: NavState | null) => {
        if (!cancelled && data) {
          setNav(data);
          if (data.userId) {
            identifyUser(data.userId, {
              user_role: data.role ?? "unknown",
            });
          }
        }
      })
      .catch(() => {
        // Network failure: keep the anonymous nav rather than break the page
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (nav.role !== "candidate") return;

    const area = pathname.startsWith("/companion")
      ? "/companion"
      : pathname.startsWith("/jobs")
        ? "/jobs"
        : pathname.startsWith("/conditions")
          ? "/conditions"
          : null;
    if (area) {
      window.localStorage.setItem("peakwave_last_visited_area", area);
    }
  }, [nav.role, pathname]);

  return (
    <>
      <Navbar
        userRole={nav.role}
        authenticated={Boolean(nav.userId)}
        unreadCount={nav.unreadCount}
        shortlistCount={nav.shortlistCount}
        candidateName={nav.candidateName}
        candidatePhotoUrl={nav.candidatePhotoUrl}
        profileId={nav.profileId}
        employerCompanyName={nav.employerCompanyName}
        employerCompanyLogo={nav.employerCompanyLogo}
        employerCompanySlug={nav.employerCompanySlug}
        crewAnyUnread={nav.crewAnyUnread}
        crewAnyMention={nav.crewAnyMention}
        hasEpicSpot={nav.hasEpicSpot}
        callsUnread={nav.callsUnread}
        workProfileVisible={nav.workProfileVisible}
      />
      {nav.role === "candidate" && <ActivityTracker />}
    </>
  );
}
